import { inject, Injectable } from '@angular/core';
import {
  KIT_FIREBASE_AUTH,
  kitAuthState,
  kitReauthWithRetry,
  kitResolveAuthStatus,
  kitSendEmailVerification,
  kitSendPasswordReset,
  kitSignIn,
  kitSignOut,
  kitSignUp,
  kitUpdateEmail,
  kitUpdatePassword,
} from '@rdlabo/ionic-angular-kit/auth-firebase';
import type { User } from '@rdlabo/ionic-angular-kit/auth-firebase';
import { HttpClient } from '@angular/common/http';
import { LoadingController, NavController } from '@ionic/angular';
import type { Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { firebaseError } from './translate';
import { KitOverlayController } from '@rdlabo/ionic-angular-kit';
import { mergeMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import type { IDatabase } from '../interfaces';
import { launchPasswordPrompt } from './modals/password-prompt/password-prompt.component';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly #afAuth = inject(KIT_FIREBASE_AUTH);
  readonly #navCtrl = inject(NavController);
  readonly #overlay = inject(KitOverlayController);
  readonly #loadingCtrl = inject(LoadingController);
  readonly #http = inject(HttpClient);

  constructor() {}

  /**
   * ログインできてるかどうかを確認
   * @param isReload メールのValidationチェック
   */
  isAuth$(isReload = false): Observable<'user' | 'confirm' | 'required'> {
    return kitAuthState(this.#afAuth).pipe(
      mergeMap(async (data) => {
        if (isReload) {
          await this.#afAuth.currentUser?.reload();
        }
        // 3-state 判定は kit へ（e2e は user 扱い）。reload は odss 固有の副作用として残置。
        return kitResolveAuthStatus(data, { allowWhen: () => environment.e2e });
      }),
    );
  }

  getState$(): Observable<User | null> {
    return kitAuthState(this.#afAuth);
  }

  async signUp(email: string, password: string, database: IDatabase): Promise<void> {
    if (database) {
      localStorage.setItem('apiKey', database);
    }
    await kitSignUp(this.#afAuth, email, password, {
      success: () => this.#overlay.presentToast({ message: '認証URLつきのメールを送信しました' }),
      error: (e) => this.#alertError(e as { code: string; message: string }),
    });
  }

  async signIn(email: string, password: string, database: IDatabase): Promise<void> {
    if (database) {
      localStorage.setItem('apiKey', database);
    }
    await kitSignIn(this.#afAuth, email, password, {
      error: (e) => this.#alertError(e as { code: string; message: string }),
    });
  }

  async signOut(): Promise<void> {
    await kitSignOut(this.#afAuth, {
      success: async () => {
        await this.#navCtrl.navigateRoot('/');
        await this.#overlay.presentToast({
          message: 'サインアウトしました',
        });
      },
      error: (e) => this.#alertError(e as { code: string; message: string }),
    });
  }

  async resetPassword(email: string): Promise<void> {
    await kitSendPasswordReset(this.#afAuth, email, {
      success: () =>
        this.#overlay.presentToast({ message: 'パスワードを再設定する方法をメールアドレスに送信しました' }),
      error: (e) => this.#alertError(e as { code: string; message: string }),
    });
  }

  async sendEmailVerification(): Promise<void> {
    await kitSendEmailVerification(this.#afAuth, {
      success: () => this.#overlay.presentToast({ message: '認証URLつきのメールを送信しました' }),
      error: (e) => this.#alertError(e as { code: string; message: string }),
    });
  }

  /**
   * メールアドレスの変更／追加。パスワード無効なら先に有効化を促す（odss 固有の前提条件）。
   * 再認証＋wrong-password 再試行の機構だけ kit `kitReauthWithRetry` に委譲し、prompt・loading・
   * エラー表示・成功後遷移は app 固有の副作用として持つ。
   * @param currentEmail 現在のメールアドレス
   * @param newEmail 新しいメールアドレス
   * @param isPassword パスワードログインが有効かどうか
   */
  async updateEmail(currentEmail: string, newEmail: string, isPassword: boolean): Promise<boolean> {
    if (!isPassword) {
      await this.#overlay.alertClose({
        header: `パスワードログインを有効化ください`,
        message: `メールアドレスを設定するには、パスワードが有効化されてる必要があります。先にパスワードログインを有効化してください。`,
      });
      return false;
    }

    const ok = await kitReauthWithRetry(this.#afAuth, currentEmail, {
      prompt: (retry) =>
        this.#promptPassword(
          {
            header: `現在のパスワード`,
            message: `メールアドレスの変更には、メール認証が必要です。メールアドレスを再度ご確認の上、変更する場合は現在のパスワードをご入力ください。`,
            wrongPassword: `現在のパスワードが間違っています`,
          },
          retry,
        ),
      mutate: (user) => kitUpdateEmail(user, newEmail),
      withLoading: (run) => this.#withLoading(`保存中`, run),
    }).catch(async (e) => {
      await this.#alertError(e as { code: string; message: string });
      return false;
    });
    if (ok) {
      await this.#navCtrl.navigateRoot('/auth/confirm');
      return true;
    }
    return false;
  }

  /**
   * パスワードの変更。再認証機構は kit `kitReauthWithRetry` に委譲、prompt・エラー表示・成功後通知は
   * app 固有の副作用。
   * @param currentEmail 現在のメールアドレス
   * @param newPassword 新しいパスワード
   */
  async updatePassword(currentEmail: string, newPassword: string): Promise<boolean> {
    const ok = await kitReauthWithRetry(this.#afAuth, currentEmail, {
      prompt: (retry) =>
        this.#promptPassword(
          {
            header: `使用中のパスワードをご入力ください`,
            message: undefined,
            wrongPassword: `使用中のパスワードが間違っています`,
          },
          retry,
        ),
      mutate: (user) => kitUpdatePassword(user, newPassword),
    }).catch(async (e) => {
      await this.#alertError(e as { code: string; message: string });
      return false;
    });
    if (ok) {
      await this.#overlay.alertClose({
        header: `保存しました`,
        message: `パスワードを変更しました。以前のものは利用することはできませんのでご注意ください`,
      });
    }
    return ok;
  }

  /** ローディング表示（副作用）で非同期処理を包む。`kitReauthWithRetry` の withLoading に渡す。 */
  async #withLoading(message: string, run: () => Promise<void>): Promise<void> {
    const loading = await this.#loadingCtrl.create({ message });
    await loading.present();
    await run().finally(() => loading.dismiss());
  }

  /**
   * 現在のパスワード確認プロンプト（副作用）。masked input＋辞書文言。`kitReauthWithRetry` から
   * 間違い時 `retry=true` で再呼び出しされる。cancel/backdrop/戻る = 全 dismiss を null に。
   */
  async #promptPassword(
    text: { header: string; message?: string; wrongPassword: string },
    retry: boolean,
  ): Promise<string | null> {
    const password = await launchPasswordPrompt(this.#overlay, {
      heading: text.header,
      message: text.message,
      retryError: retry ? text.wrongPassword : undefined,
    });
    return password ?? null;
  }

  /**
   * アラートの統一（副作用）。firebase エラーコードを辞書引きして表示。seatkeep 正本に合わせる。
   */
  async #alertError(e: { code: string; message: string }): Promise<void> {
    if (Object.prototype.hasOwnProperty.call(firebaseError, e.code)) {
      const key = e.code as keyof typeof firebaseError;
      await this.#overlay.alertClose({ header: firebaseError[key].code, message: firebaseError[key].message });
      return;
    }
    await this.#overlay.alertClose({ header: e.code, message: e.message });
  }

  //有効なemailかどうかチェックする
  async checkExistsEmail(email: string, database: IDatabase): Promise<boolean | null> {
    if (database) {
      localStorage.setItem('apiKey', database);
    }
    return firstValueFrom(this.#http.get<void>(environment.api() + 'email_exists/' + encodeURIComponent(email)))
      .then(() => true)
      .catch(async (error: { status?: number }) => {
        if (error.status === 404) {
          return false;
        }
        await this.#overlay.alertClose({
          header: 'メールアドレスの確認に失敗しました',
          message: '通信環境を確認して、もう一度お試しください。',
        });
        return null;
      });
  }
}
